import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { NomeVenditore } from "@/components/NomeVenditore";
import { haFlag } from "@/lib/ambito";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, Trash2, FileText, Sparkles, ArrowUp, ArrowDown, X } from "lucide-react";
import VehicleStatusBadge from "@/components/VehicleStatusBadge";
import TipoCaricoBadge from "@/components/TipoCaricoBadge";
import CestinoModal from "@/components/CestinoModal";
import VehicleColumnsButton from "@/components/VehicleColumnsButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { tipoCaricoCompact, VEHICLE_TIPO_CARICO_LABELS } from "@/lib/labels";
import { toast } from "sonner";
import { formatEUR } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import AvvisoPagina from "@/components/AvvisoPagina";
import StickyHScroll from "@/components/StickyHScroll";
import CopertinaThumb from "@/components/CopertinaThumb";
import MostraFotoToggle, { useMostraFoto } from "@/components/MostraFotoToggle";
import { rigaCliccabile, RIGA_CLS } from "@/lib/rigaCliccabile";
import { Cella, fmtData, useLarghezzeAuto, useUltimaRichiesta, attrRicerca } from "@/lib/listaColonne";

// Sprint DASHBOARD-FIX-2 — colonne di servizio in carattere piccolo (12px, la misura di «Data contratto»)
const SERVIZIO = new Set(["codice", "data", "azienda", "sede", "cliente", "venditore"]);

const STATUS_PRATICA_LABELS = {
  draft: "Bozza", impegnata: "Impegnata", venduta: "Venduta",
  consegnata: "Consegnata", annullata: "Annullata",
};
const STATUS_PREV_LABELS = {
  draft: "Bozza", in_corso: "In corso", scaduto: "Scaduto",
  confermato: "Confermato", annullato: "Annullato",
};

function StatusBadge({ tipo, status }) {
  const labels = tipo === "preventivo" ? STATUS_PREV_LABELS : STATUS_PRATICA_LABELS;
  const palette = {
    draft: "bg-slate-100 text-slate-700",
    impegnata: "bg-amber-100 text-amber-800",
    venduta: "bg-red-100 text-red-800",
    consegnata: "bg-violet-100 text-violet-800",
    annullata: "bg-rose-100 text-rose-800",
    in_corso: "bg-amber-100 text-amber-800",
    scaduto: "bg-rose-100 text-rose-800",
    confermato: "bg-emerald-100 text-emerald-800",
    annullato: "bg-rose-100 text-rose-800",
  };
  return (
    <span className={`px-2 py-0.5 text-[11px] font-medium rounded-full ${palette[status] || "bg-slate-100"}`}>
      {labels[status] || status}
    </span>
  );
}

// Sprint 4Z.1 G1 — Definizione colonne personalizzabili.
// `applicableFor`: "preventivo" | "pratica" | "both".
// `defaultHidden`: nascosta inizialmente (l'utente la attiva via "Personalizza colonne").
function buildColumns({ tipo, canSeeMargine, helpers }) {
  const isPratica = tipo === "pratica";
  const { renderClienteName, venditoreName } = helpers;
  const cols = [
    // Sprint 4Z.2 E1 — TipoCaricoBadge come PRIMA colonna fissa (alwaysShow)
    { key: "tipo_carico_badge", label: "T.C.", sortField: "tipo_carico", alwaysShow: true, width: 70,
      render: (v) => <TipoCaricoBadge value={v.vehicle_tipo_carico} size="sm" /> },
    { key: "codice", label: "N° Contratto", alwaysShow: true, sortField: "codice",
      render: (v) => <span className="font-mono text-[13px]">{v.codice}</span> },
    // Sprint 4Z.1 C1: per preventivo mostra data_creazione (created_at), per pratica data_contratto
    { key: "data", label: isPratica ? "Data contratto" : "Data preventivo",
      sortField: isPratica ? "data" : "data_creazione",
      render: (v) => {
        const val = isPratica ? v.data_contratto : (v.created_at || "").slice(0, 10);
        return <span className="font-mono text-[12px] text-slate-600">{fmtData(val)}</span>;
      } },
    // Sprint 4Z.2 B4+B5+E2: 2 colonne SEPARATE Disp. e Stato
    { key: "disp_fisica", label: "Disp.", sortField: "disp_fisica", width: 110,
      render: (v) => (
        <span className="inline-flex" data-testid={`vendita-dispfisica-${v.id}`}>
          <DispFisicaPill value={v.vehicle_disponibilita_fisica} />
        </span>
      ) },
    { key: "stato", label: "Stato", sortField: "status",
      render: (v) => isPratica
        ? <StatoCommercialePill status={v.status} />
        : <StatusBadge tipo={v.tipo} status={v.status} /> },
    { key: "cliente", label: "Cliente", sortField: "cliente",
      render: (v) => v.cliente_id
        ? <span>{renderClienteName(v.cliente_id)}</span>
        : <span className="italic text-amber-700">Anonimo</span> },
    { key: "venditore", label: "Venditore", sortField: "venditore",
      render: (v) => <span className="text-slate-700"><NomeVenditore vendita={v} testid={`venditore-${v.id}`} /></span> },
    { key: "targa", label: "Targa", sortField: "targa",
      render: (v) => <span className="font-mono text-[12px]">{v.vehicle_snapshot?.targa || "—"}</span> },
    { key: "veicolo", label: "Veicolo", sortField: "veicolo",
      render: (v) => {
        const s = v.vehicle_snapshot || {};
        return (<>
          <div className="font-medium">{s.marca} {s.modello}</div>
          {s.versione && <div className="text-[11px] text-slate-500">{s.versione}</div>}
        </>);
      } },
    // Sprint 4Z.1 E3
    { key: "azienda", label: "Azienda", sortField: "azienda", applicableFor: "pratica", defaultHidden: true,
      render: (v) => <span className="text-[12px] text-slate-700">{v.vehicle_company_name || "—"}</span> },
    { key: "sede", label: "Sede", sortField: "sede", applicableFor: "pratica", defaultHidden: true,
      render: (v) => <span className="text-[12px] text-slate-700">{v.vehicle_site_name || "—"}</span> },
    { key: "prezzo_vendita", label: "Prezzo vendita", align: "right", sortField: "totale",
      render: (v) => <span className="font-mono text-blue-700">{formatEUR(v.margine?.totale_vendita_finale)}</span> },
    { key: "assicurazione", label: "Assicurazione CVT", align: "right", sortField: "assicurazione_cvt", defaultHidden: true,
      render: (v) => v.margine?.cvt_totale_cliente
        ? <span className="font-mono">{formatEUR(v.margine.cvt_totale_cliente)}</span>
        : <span className="text-slate-400">—</span> },
    { key: "finanziato", label: "Finanziato", sortField: "finanziato", align: "right",
      render: (v) => v.finanziamento?.importo_finanziato
        ? <span className="font-mono">{formatEUR(v.finanziamento.importo_finanziato)}</span>
        : <span className="text-slate-400">—</span> },
  ];
  if (canSeeMargine) {
    cols.push({ key: "margine", label: "Margine €", align: "right",
      render: (v) => {
        const m = v.margine?.margine_netto;
        const color = (m === null || m === undefined) ? "text-slate-400"
          : m >= 0 ? "text-emerald-700" : "text-rose-700";
        return (
          <span className={`inline-flex items-center gap-1.5 justify-end font-mono font-semibold ${color}`}
                data-testid={`vendita-margine-${v.id}`}>
            {(m === null || m === undefined) ? "—" : formatEUR(m)}
            {v.forced_sotto_margine && (
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-orange-500 ring-2 ring-orange-200"
                    title="Vendita confermata sotto margine minimo (forzatura)"
                    data-testid={`vendita-forced-dot-${v.id}`} />
            )}
          </span>
        );
      } });
  }
  return cols.filter((c) => !c.applicableFor || c.applicableFor === tipo || c.applicableFor === "both");
}

// Sprint 4Z.2 B4+B5+E2 — Pill per disp_fisica (asse-1) e stato_commerciale (asse-2)
function DispFisicaPill({ value }) {
  const map = {
    VIRTUAL: { label: "Virtuale", color: "bg-slate-100 text-slate-700 border-slate-200" },
    INCOMING: { label: "In arrivo", color: "bg-cyan-100 text-cyan-800 border-cyan-200" },
    AVAILABLE: { label: "Disponibile", color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  };
  if (!value) return <span className="text-slate-300">—</span>;
  const cfg = map[value] || { label: value, color: "bg-slate-100 text-slate-600 border-slate-200" };
  return (
    <span className={`inline-flex items-center font-medium rounded-full border px-2 py-0.5 text-[11px] ${cfg.color}`}
          data-testid={`disp-fisica-pill-${value}`}>
      {cfg.label}
    </span>
  );
}

function StatoCommercialePill({ status }) {
  const map = {
    draft: { label: "Bozza", color: "bg-slate-100 text-slate-700 border-slate-200" },
    impegnata: { label: "Impegnata", color: "bg-amber-100 text-amber-800 border-amber-200" },
    venduta: { label: "Venduta", color: "bg-red-100 text-red-800 border-red-200" },
    consegnata: { label: "Consegnata", color: "bg-violet-100 text-violet-800 border-violet-200" },
    annullata: { label: "Annullata", color: "bg-rose-100 text-rose-700 border-rose-200" },
  };
  if (!status) return <span className="text-slate-300">—</span>;
  const cfg = map[status] || { label: status, color: "bg-slate-100 text-slate-600 border-slate-200" };
  return (
    <span className={`inline-flex items-center font-medium rounded-full border px-2 py-0.5 text-[11px] ${cfg.color}`}
          data-testid={`stato-commerciale-pill-${status}`}>
      {cfg.label}
    </span>
  );
}

export default function Vendite() {
  const navigate = useNavigate();
  const location = useLocation();
  const qs = new URLSearchParams(location.search);
  const forcedTipo = qs.get("tipo");
  // Sprint DASHBOARD-FIX — filtro di stato solo dall'indirizzo (parametro già accettato da GET /vendite)
  const statusUrl = qs.get("status");
  const togliStatus = () => {
    const n = new URLSearchParams(location.search);
    n.delete("status");
    navigate(`${location.pathname}?${n.toString()}`, { replace: true });
  };
  const { user } = useAuth();
  const [mostraFoto, setMostraFoto] = useMostraFoto();

  // Sprint 4X.1 (Problema 3): tipo deve seguire l'URL ad ogni cambio (preventivi↔pratiche).
  const tipo = forcedTipo || "pratica";
  // Sprint 4Z.1 E2: default "Mostra anche impegnate" attivo per pratiche
  const [includeImpegnate, setIncludeImpegnate] = useState(true);
  // Sprint 4Z.1 F1: toggle "Mostra annullate" (default OFF)
  const [includeAnnullate, setIncludeAnnullate] = useState(false);
  const [q, setQ] = useState("");
  const [dataDa, setDataDa] = useState("");
  const [dataA, setDataA] = useState("");
  const [venditoreFilter, setVenditoreFilter] = useState("__all");
  const [onlyForced, setOnlyForced] = useState(false);
  const [dispFisicaFilter, setDispFisicaFilter] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [clientiMap, setClientiMap] = useState({});
  const [users, setUsers] = useState([]);
  // Sprint 4Z C3/E1: stato modale "cestina riga"
  const [cestinaTarget, setCestinaTarget] = useState(null); // { id, codice, tipo, status }
  const [cestinaSubmitting, setCestinaSubmitting] = useState(false);
  // Sprint 4Z.2 B3: modal Cestino unificato
  const [cestinoModalOpen, setCestinoModalOpen] = useState(false);
  // Sprint 4Z.2 B2.1: sort persistito
  const sortLsKey = `vendite-sort-${tipo || "pratica"}`;
  const nuovaRichiesta = useUltimaRichiesta();
  const [sortState, setSortState] = useState(() => {
    try {
      const raw = localStorage.getItem(sortLsKey);
      if (raw) return JSON.parse(raw);
    } catch {}
    return { by: null, order: null };
  });
  useEffect(() => {
    try {
      const raw = localStorage.getItem(sortLsKey);
      setSortState(raw ? JSON.parse(raw) : { by: null, order: null });
    } catch { setSortState({ by: null, order: null }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo]);
  const cycleSort = (sortField) => {
    if (!sortField) return;
    let next;
    if (sortState.by !== sortField) next = { by: sortField, order: "asc" };
    else if (sortState.order === "asc") next = { by: sortField, order: "desc" };
    else next = { by: null, order: null };
    setSortState(next);
    try { localStorage.setItem(sortLsKey, JSON.stringify(next)); } catch {}
  };
  // Sprint 4Z.2 B2.2: resize colonne persistito
  const widthsLsKey = `vendite-widths-${tipo || "pratica"}`;
  const [colWidths, setColWidths] = useState(() => {
    try {
      const raw = localStorage.getItem(widthsLsKey);
      if (raw) return JSON.parse(raw);
    } catch {}
    return {};
  });
  useEffect(() => {
    try {
      const raw = localStorage.getItem(widthsLsKey);
      setColWidths(raw ? JSON.parse(raw) : {});
    } catch { setColWidths({}); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo]);
  const persistWidth = (key, w) => {
    setColWidths((prev) => {
      const next = { ...prev, [key]: Math.max(60, w) };
      try { localStorage.setItem(widthsLsKey, JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const resetWidths = () => {
    setColWidths({});
    try { localStorage.removeItem(widthsLsKey); } catch {}
  };

  const handleCestina = async () => {
    if (!cestinaTarget) return;
    setCestinaSubmitting(true);
    try {
      const r = await api.post(`/vendite/${cestinaTarget.id}/cestina`, {
        stato_partenza: "attiva",
        motivo: "Cestinato da lista",
      });
      const action = r.data?.action;
      if (action === "soft_deleted") {
        toast.success(`Preventivo ${cestinaTarget.codice} eliminato`);
      } else if (action === "annullata") {
        toast.success(`Pratica ${cestinaTarget.codice} annullata`);
      } else {
        toast.success("Operazione completata");
      }
      setCestinaTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore operazione cestino");
    } finally {
      setCestinaSubmitting(false);
    }
  };

  const isPratica = tipo === "pratica";

  // Permesso "gestione_incassi" su almeno 1 azienda → mostra colonna Margine €
  const canSeeMargine = useMemo(() => haFlag(user, "gestione_incassi"), [user]);

  // Carica utenti per il filtro venditore + nome venditore in tabella
  useEffect(() => {
    api.get("/users?page_size=300&include_inactive=true").then((r) => {
      setUsers(r.data.items || r.data || []);
    }).catch(() => {});
  }, []);
  const usersMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const venditoreName = (uid) => {
    const u = usersMap[uid];
    if (!u) return uid?.slice(0, 8) || "—";
    return [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email || u.id?.slice(0, 8);
  };

  const load = () => {
    const p = new URLSearchParams();
    if (tipo) p.set("tipo", tipo);
    if (q) p.set("q", q);
    if (venditoreFilter && venditoreFilter !== "__all") p.set("venditore_id", venditoreFilter);
    if (onlyForced) p.set("forced_sotto_margine", "true");
    if (statusUrl) p.set("status", statusUrl);
    // Sprint 4Z.2 B2.1: sort
    if (sortState.by) {
      p.set("sort_by", sortState.by);
      p.set("sort_order", sortState.order || "asc");
    }
    p.set("page_size", "300");
    setLoading(true);
    const ultima = nuovaRichiesta();
    api.get(`/vendite?${p.toString()}`)
      .then((r) => {
        if (!ultima()) return;
        let arr = r.data.items || [];
        if (isPratica) {
          if (!includeImpegnate) {
            arr = arr.filter((v) => ["venduta", "consegnata"].includes(v.status)
              || (includeAnnullate && v.status === "annullata"));
          } else {
            // Sprint 4Z.1 F1: include annullate solo se toggle ON
            arr = arr.filter((v) => v.status !== "annullata" || includeAnnullate);
          }
        }
        // Sprint 4Z.1 C1: per preventivi filtra per data_creazione (created_at), per pratiche per data_contratto
        const dateField = isPratica ? "data_contratto" : "created_at";
        if (dataDa) arr = arr.filter((v) => !v[dateField] || v[dateField] >= dataDa);
        if (dataA) arr = arr.filter((v) => !v[dateField] || v[dateField] <= dataA);
        if (dispFisicaFilter.length > 0) {
          arr = arr.filter((v) => dispFisicaFilter.includes(v.vehicle_disponibilita_fisica));
        }
        setItems(arr);
        setTotal(arr.length);
        const ids = [...new Set(arr.map((v) => v.cliente_id).filter(Boolean))];
        const missing = ids.filter((id) => !clientiMap[id]);
        if (missing.length > 0) {
          Promise.all(missing.slice(0, 80).map((id) => api.get(`/anagrafiche/${id}`).then((rr) => rr.data).catch(() => null)))
            .then((arrA) => {
              const map = { ...clientiMap };
              arrA.forEach((a) => { if (a) map[a.id] = a; });
              setClientiMap(map);
            });
        }
      })
      .finally(() => { if (ultima()) setLoading(false); });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [tipo, statusUrl, includeImpegnate, includeAnnullate, dataDa, dataA, venditoreFilter, onlyForced, dispFisicaFilter, sortState]);

  // Sprint 4X.2 (Sezione 6): ricerca full-text con debounce 300ms
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const renderClienteName = (cid) => {
    const a = clientiMap[cid];
    if (!a) return cid?.slice(0, 8) || "—";
    if (a.tipo_persona === "azienda") return a.ragione_sociale || cid?.slice(0, 8);
    return [a.cognome, a.nome].filter(Boolean).join(" ") || cid?.slice(0, 8);
  };

  // Sprint 4Z.1 G1 — Build colonne + state visibilità (persistito in localStorage)
  const allColumns = useMemo(
    () => buildColumns({ tipo, canSeeMargine, helpers: { renderClienteName, venditoreName } }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tipo, canSeeMargine, clientiMap, usersMap],
  );
  const colsLsKey = `vendite-cols-${tipo}`;
  const [visibleColsMap, setVisibleColsMap] = useState(() => {
    try {
      const raw = localStorage.getItem(colsLsKey);
      if (raw) return JSON.parse(raw);
    } catch {}
    // default: tutte tranne defaultHidden
    return Object.fromEntries(
      buildColumns({ tipo, canSeeMargine, helpers: { renderClienteName: () => "", venditoreName: () => "" } })
        .map((c) => [c.key, !c.defaultHidden]),
    );
  });
  // Reset quando cambia tipo (chiavi colonne diverse)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(colsLsKey);
      if (raw) { setVisibleColsMap(JSON.parse(raw)); return; }
    } catch {}
    setVisibleColsMap(Object.fromEntries(
      allColumns.map((c) => [c.key, !c.defaultHidden]),
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo]);
  const persistVisible = (m) => {
    setVisibleColsMap(m);
    try { localStorage.setItem(colsLsKey, JSON.stringify(m)); } catch {}
  };

  // Sprint 4Z.2.1: order delle colonne (drag&drop nel popover) persistito
  const orderLsKey = `vendite-order-${tipo}`;
  const [orderColsList, setOrderColsList] = useState(() => {
    try {
      const raw = localStorage.getItem(orderLsKey);
      if (raw) return JSON.parse(raw);
    } catch {}
    return null;  // null → usa ordine COLUMN_DEFS
  });
  useEffect(() => {
    try {
      const raw = localStorage.getItem(orderLsKey);
      setOrderColsList(raw ? JSON.parse(raw) : null);
    } catch { setOrderColsList(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo]);
  const persistOrder = (arr) => {
    setOrderColsList(arr);
    try { localStorage.setItem(orderLsKey, JSON.stringify(arr)); } catch {}
  };

  const orderedColumns = useMemo(() => {
    if (!orderColsList || !orderColsList.length) return allColumns;
    const byKey = Object.fromEntries(allColumns.map((c) => [c.key, c]));
    const ordered = orderColsList.map((k) => byKey[k]).filter(Boolean);
    // Append eventuali colonne non in order (nuove dopo upgrade)
    for (const c of allColumns) if (!ordered.includes(c)) ordered.push(c);
    return ordered;
  }, [allColumns, orderColsList]);
  const visibleColumns = orderedColumns.filter((c) => c.alwaysShow || visibleColsMap[c.key] !== false);
  // Sprint DASHBOARD-FIX-2 — larghezza: misura a mano dell'utente, altrimenti adattata al contenuto
  const tableRef = useRef(null);
  const autoW = useLarghezzeAuto(tableRef, [items, visibleColumns.map((c) => c.key).join(), mostraFoto]);
  const larg = (c) => colWidths[c.key] ?? autoW[c.key] ?? c.width;
  const larghezzaTot = visibleColumns.reduce((t, c) => t + (larg(c) || 120), (mostraFoto ? 88 : 0) + 56);

  return (
    <div className="space-y-6" data-testid="vendite-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl text-slate-900">
            {isPratica ? "Pratiche" : "Preventivi"}
          </h1>
          <p className="text-sm text-slate-500">
            {total} {isPratica ? "pratiche" : "preventivi"} · vista commerciale Sprint 4A.2
          </p>
        </div>
      </div>

      {/* Sprint UI-GLOBALE — informazione senza comando: centro avvisi */}
      <AvvisoPagina id={`vendite-info-${isPratica ? "pratiche" : "preventivi"}`} livello="info"
        testo={isPratica
          ? "Visualizzazione veicoli con vendita confermata. Per modificare una pratica apri la scheda veicolo corrispondente."
          : "Le pratiche di vendita vengono create automaticamente al momento dell'impegno di un veicolo."} />

      <Card className="p-4 space-y-3">
        <div className="flex flex-wrap gap-3 items-end">
          {statusUrl && (
            <div className="flex items-center gap-2 px-3 py-2 border border-blue-300 bg-blue-50 rounded-md text-sm text-blue-900" data-testid="filtro-status-url">
              Filtro attivo: stato <b>{(isPratica ? STATUS_PRATICA_LABELS : STATUS_PREV_LABELS)[statusUrl] || statusUrl}</b>
              <button type="button" onClick={togliStatus} className="ml-1 rounded p-0.5 hover:bg-blue-100" aria-label="Togli il filtro di stato"
                data-testid="filtro-status-url-togli"><X className="h-3.5 w-3.5" /></button>
            </div>
          )}
          {isPratica && (
            <div className="flex items-center gap-2 px-3 py-2 border rounded-md bg-slate-50">
              <Switch checked={includeImpegnate} onCheckedChange={setIncludeImpegnate} data-testid="toggle-include-impegnate" />
              <Label className="text-sm cursor-pointer">Mostra anche impegnate</Label>
            </div>
          )}
          {isPratica && canSeeMargine && (
            <div className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-md">
              <Switch checked={onlyForced} onCheckedChange={setOnlyForced} data-testid="toggle-only-forced-sotto-margine" />
              <Label className="text-sm cursor-pointer flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full bg-orange-500" />
                Solo forzate sotto margine
              </Label>
            </div>
          )}
          <div className="min-w-[200px]">
            <Label>Venditore</Label>
            <Select value={venditoreFilter} onValueChange={setVenditoreFilter}>
              <SelectTrigger data-testid="vendite-venditore-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">Tutti i venditori</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id} data-testid={`vendite-venditore-opt-${u.id}`}>
                    <NomeVenditore vendita={{ venditore_id: u.id, venditore_stato: u.is_active === false ? "disattivato" : "attivo",
                      venditore_nome: [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email }} testid={`vendite-venditore-nome-${u.id}`} />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Da ({isPratica ? "data contratto" : "data preventivo"})</Label>
            <Input type="date" value={dataDa} onChange={(e) => setDataDa(e.target.value)} className="w-[160px]" />
          </div>
          <div>
            <Label>A ({isPratica ? "data contratto" : "data preventivo"})</Label>
            <Input type="date" value={dataA} onChange={(e) => setDataA(e.target.value)} className="w-[160px]" />
          </div>
          <div>
            <Label>Disp. Fisica veicolo</Label>
            <div className="flex gap-1 mt-1" data-testid="filter-vendite-dispfisica">
              {[
                ["VIRTUAL", "V"], ["INCOMING", "I"], ["AVAILABLE", "D"],
              ].map(([k, lbl]) => (
                <button
                  key={k} type="button"
                  onClick={() => setDispFisicaFilter((cur) =>
                    cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]
                  )}
                  title={k === "VIRTUAL" ? "Virtuale" : k === "INCOMING" ? "In arrivo" : "Disponibile"}
                  className={`text-[11px] w-7 h-7 rounded-full border ${
                    dispFisicaFilter.includes(k)
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                  }`}
                  data-testid={`filter-vendite-dispfisica-${k}`}
                >{lbl}</button>
              ))}
            </div>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex-1 min-w-[220px]">
            <Label>Ricerca</Label>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="codice, targa, cliente..." className="pl-8" data-testid="vendite-search" {...attrRicerca(tipo || "vendite")} />
            </div>
          </form>
          <Button variant="outline" onClick={() => setCestinoModalOpen(true)} data-testid="vendite-cestino-btn">
            <Trash2 className="h-4 w-4 mr-1.5" /> Cestino
          </Button>
          {/* Sprint 4Z.1 F1: toggle Mostra annullate (solo per pratiche) */}
          {isPratica && (
            <div className="flex items-center gap-2 px-2 py-1 rounded-md border border-slate-200 self-end h-9"
                 data-testid="toggle-mostra-annullate-wrap">
              <Switch checked={includeAnnullate} onCheckedChange={setIncludeAnnullate}
                      data-testid="toggle-mostra-annullate" />
              <span className="text-[12px] text-slate-700">Mostra annullate</span>
            </div>
          )}
          {/* Sprint 4Z.1 G1: personalizza colonne */}
          {/* Sprint 4Z.2.1: VehicleColumnsButton riusato (drag&drop ordine colonne) */}
          <MostraFotoToggle mostra={mostraFoto} onChange={setMostraFoto} />
          <VehicleColumnsButton
            allColumns={allColumns.map((c) => ({ key: c.key, label: c.label }))}
            order={(orderColsList && orderColsList.length)
              ? orderColsList
              : allColumns.map((c) => c.key)}
            visible={allColumns.filter((c) => c.alwaysShow || visibleColsMap[c.key] !== false).map((c) => c.key)}
            onChange={({ order, visible }) => {
              persistOrder(order);
              const next = Object.fromEntries(allColumns.map((c) => [c.key, c.alwaysShow ? true : visible.includes(c.key)]));
              persistVisible(next);
            }}
            onReset={() => {
              persistVisible(Object.fromEntries(allColumns.map((c) => [c.key, !c.defaultHidden])));
              persistOrder(allColumns.map((c) => c.key));
            }}
            onResetWidths={resetWidths}
          />
          {/* Sprint 4X.4 E6: cleanup preventivi scaduti — solo developer/admin, solo nella vista Preventivi */}
          {!isPratica && ["developer", "admin"].includes(user?.role_default || (user?.role || "").toLowerCase()) && (
            <BatchCleanupExpiredButton onCleaned={load} />
          )}
        </div>
      </Card>

      <Card>
        <StickyHScroll testid="vendite-hscroll">
          <table ref={tableRef} className="w-full text-sm lista-uniforme" data-testid="vendite-table"
                 style={{ tableLayout: "fixed", minWidth: larghezzaTot }}>
            <thead className="bg-slate-50 border-b">
              <tr className="text-left text-[12px] text-slate-600 uppercase tracking-wide">
                {mostraFoto && <th className="px-3 py-2 w-[88px]" data-testid="col-h-foto">Foto</th>}
                {visibleColumns.map((c) => {
                  const w = larg(c);
                  const isSortable = !!c.sortField;
                  const isActive = sortState.by === c.sortField;
                  return (
                    <th key={c.key} data-col={c.key}
                        className={`px-3 py-2 relative select-none whitespace-nowrap overflow-hidden ${c.align === "right" ? "text-right" : ""} ${isSortable ? "cursor-pointer hover:bg-slate-100" : ""}`}
                        style={w ? { width: w, minWidth: 60 } : undefined}
                        onClick={isSortable ? () => cycleSort(c.sortField) : undefined}
                        data-testid={`col-h-${c.key}`}>
                      <span className="th-label inline-flex items-center gap-1">
                        {c.label}
                        {isSortable && isActive && sortState.order === "asc" && <ArrowUp className="h-3 w-3" />}
                        {isSortable && isActive && sortState.order === "desc" && <ArrowDown className="h-3 w-3" />}
                      </span>
                      {/* Sprint 4Z.2 B2.2 — resize handle */}
                      <span
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          const startX = e.clientX;
                          const th = e.currentTarget.parentElement;
                          const startW = th.getBoundingClientRect().width;
                          const onMove = (ev) => {
                            persistWidth(c.key, startW + (ev.clientX - startX));
                          };
                          const onUp = () => {
                            window.removeEventListener("mousemove", onMove);
                            window.removeEventListener("mouseup", onUp);
                          };
                          window.addEventListener("mousemove", onMove);
                          window.addEventListener("mouseup", onUp);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-blue-300 active:bg-blue-500"
                        data-testid={`col-resize-${c.key}`}
                      />
                    </th>
                  );
                })}
                <th className="px-3 py-2 w-12 text-center">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={visibleColumns.length + 1 + (mostraFoto ? 1 : 0)} className="px-3 py-6 text-center text-slate-400">Caricamento...</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={visibleColumns.length + 1 + (mostraFoto ? 1 : 0)} className="px-3 py-12 text-center text-slate-400">
                  <FileText className="h-10 w-10 mx-auto mb-2 opacity-40" />
                  Nessuna {isPratica ? "pratica" : "preventivo"} trovat{isPratica ? "a" : "o"}
                </td></tr>
              )}
              {items.map((v) => (
                <tr
                  key={v.id}
                  {...rigaCliccabile(() => {
                    // Sprint 4Z.2 B1: pratiche annullate o senza vehicle_id → /vendite/{id}
                    // (evita che VehicleDetail apra una nuova vendita vuota perché la pratica non è "attiva")
                    const goDirect = v.status === "annullata" || !v.vehicle_id || v.deleted_at;
                    if (goDirect) navigate(`/vendite/${v.id}`);
                    else navigate(`/vehicles/${v.vehicle_id}?tab=vendita&vid=${v.id}`);
                  })}
                  className={`hover:bg-slate-50 ${RIGA_CLS} ${v.status === "annullata" ? "bg-rose-50/30" : ""}`}
                  data-testid={`vendita-row-${v.id}`}
                >
                  {mostraFoto && (
                    <td className="px-3 py-2"><CopertinaThumb vehicleId={v.vehicle_id} coverId={v.vehicle_cover} /></td>
                  )}
                  {visibleColumns.map((c) => (
                    <td key={c.key} data-col={c.key}
                        className={`px-3 py-2 align-top ${c.align === "right" ? "text-right" : ""}`}
                        style={larg(c) ? { width: larg(c) } : undefined}>
                      <Cella servizio={SERVIZIO.has(c.key)}>{c.render(v)}</Cella>
                    </td>
                  ))}
                  <td className="px-3 py-2 text-center">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCestinaTarget({
                          id: v.id, codice: v.codice,
                          tipo: v.tipo, status: v.status,
                        });
                      }}
                      className="text-slate-400 hover:text-rose-600 p-1 rounded hover:bg-rose-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      title={v.tipo === "preventivo" ? "Elimina preventivo" : "Annulla pratica"}
                      data-testid={`btn-row-trash-${v.id}`}
                      disabled={v.status === "annullata" || v.deleted_at}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </StickyHScroll>
      </Card>

      <Card className="overflow-hidden" hidden data-testid="vendite-card-legacy" />

      {/* Sprint 4Z C3/E1 — Modale conferma cestina riga */}
      <Dialog open={!!cestinaTarget} onOpenChange={(o) => !o && setCestinaTarget(null)}>
        <DialogContent className="sm:max-w-md" data-testid="cestina-row-modal">
          <DialogHeader>
            <DialogTitle>
              {cestinaTarget?.tipo === "preventivo"
                ? "Elimina preventivo"
                : "Annulla pratica"}
            </DialogTitle>
            <DialogDescription>
              {cestinaTarget?.tipo === "preventivo" ? (
                <>
                  Eliminare il preventivo <span className="font-mono font-semibold">{cestinaTarget?.codice}</span>?
                  Verrà spostato nel cestino (soft-delete) e potrà essere ripristinato da admin.
                </>
              ) : (
                <>
                  Annullare la pratica <span className="font-mono font-semibold">{cestinaTarget?.codice}</span>?
                  Lo stato passerà ad <strong>annullata</strong>. I dati restano in archivio
                  (nessuna eliminazione fisica).
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline"
                    onClick={() => setCestinaTarget(null)}
                    disabled={cestinaSubmitting}
                    data-testid="cestina-row-cancel-btn">
              Annulla
            </Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={handleCestina}
              disabled={cestinaSubmitting}
              data-testid="cestina-row-confirm-btn"
            >
              {cestinaSubmitting
                ? "Attendere..."
                : (cestinaTarget?.tipo === "preventivo" ? "Elimina" : "Annulla pratica")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sprint 4Z.2 B3 — Modal Cestino unificato */}
      <CestinoModal
        open={cestinoModalOpen}
        onOpenChange={setCestinoModalOpen}
        tipo={tipo || "pratica"}
        isAdmin={["developer", "admin"].includes(user?.role_default || (user?.role || "").toLowerCase())}
        onChange={load}
      />
    </div>
  );
}


// Sprint 4X.4 E6 — Modale "Pulisci preventivi scaduti"
function BatchCleanupExpiredButton({ onCleaned }) {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState("90");
  const [customDate, setCustomDate] = useState("");
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const computeThreshold = () => {
    if (preset === "custom") return customDate;
    const days = parseInt(preset, 10);
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 10);
  };

  const fetchPreview = async () => {
    const td = computeThreshold();
    if (!td) { toast.error("Specifica una data"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/vendite/preventivi/batch_cleanup",
        { threshold_date: td, dry_run: true });
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore anteprima");
    } finally { setSubmitting(false); }
  };

  const confirm = async () => {
    if (!preview) return;
    setSubmitting(true);
    try {
      const r = await api.post("/vendite/preventivi/batch_cleanup",
        { threshold_date: preview.threshold_date, dry_run: false });
      toast.success(`${r.data.count} preventivi soft-deleted`);
      setOpen(false); setPreview(null);
      onCleaned?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <Button variant="outline"
              onClick={() => { setOpen(true); setPreview(null); }}
              data-testid="btn-batch-cleanup-expired"
              className="border-amber-300 text-amber-700 hover:bg-amber-50">
        <Sparkles className="h-4 w-4 mr-1.5" /> Pulisci scaduti
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="batch-cleanup-modal">
          <DialogHeader>
            <DialogTitle>Pulisci preventivi scaduti</DialogTitle>
            <DialogDescription>
              Soft-delete dei preventivi con stato scaduto o annullato e
              data scadenza precedente alla soglia.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Scaduti da almeno</Label>
              <Select value={preset} onValueChange={(v) => { setPreset(v); setPreview(null); }}>
                <SelectTrigger data-testid="batch-cleanup-preset"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 giorni</SelectItem>
                  <SelectItem value="90">90 giorni</SelectItem>
                  <SelectItem value="180">180 giorni</SelectItem>
                  <SelectItem value="360">360 giorni</SelectItem>
                  <SelectItem value="custom">Data libera...</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {preset === "custom" && (
              <div>
                <Label>Threshold (YYYY-MM-DD)</Label>
                <Input type="date" value={customDate}
                       onChange={(e) => { setCustomDate(e.target.value); setPreview(null); }}
                       data-testid="batch-cleanup-custom-date" />
              </div>
            )}
            <Button variant="outline" onClick={fetchPreview} disabled={submitting}
                    data-testid="batch-cleanup-preview-btn">
              {submitting ? "Caricamento..." : "Anteprima"}
            </Button>
            {preview && (
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm" data-testid="batch-cleanup-preview-result">
                Verranno eliminati <strong>{preview.count}</strong> preventivi
                (scaduti prima del {preview.threshold_date}).
                {preview.examples?.length > 0 && (
                  <ul className="mt-2 text-xs">
                    {preview.examples.slice(0, 5).map((e, i) => (
                      <li key={i}>{e.codice} — scad. {e.data_scadenza} ({e.status})</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white"
                    disabled={!preview || preview.count === 0 || submitting}
                    onClick={confirm}
                    data-testid="batch-cleanup-confirm-btn">
              Elimina {preview?.count ? `(${preview.count})` : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

